"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
const _ = require("lodash");
const math = require("mathjs");
const uuidv4 = require("uuid/v4");
const uris = require("../globals/uris");
const globals_1 = require("./globals");
const manager_1 = require("../manager");
/**
 * Offers basic functions for generating dymos, inserts them into the given store.
 */
class DymoGenerator {
    constructor(useUuids = true, store = new manager_1.DymoManager().getStore()) {
        this.useUuids = useUuids;
        this.store = store;
        this.summarizingMode = globals_1.SUMMARY.MEAN;
        this.dymoCount = 0;
        this.renderingCount = 0;
    }
    getStore() {
        return this.store;
    }
    getTopDymoJsonld() {
        return this.store.uriToJsonld(this.currentTopDymo);
    }
    getRenderingJsonld() {
        return this.store.uriToJsonld(this.currentRenderingUri);
    }
    resetDymo() {
        this.currentTopDymo = undefined; //the top dymo for the current audio file
        //this.internalAddFeature("level", uris.LEVEL_FEATURE, 0, 0);
        //this.internalAddFeature("random", null, 0, 1);
    }
    addRendering(dymoUri = this.currentTopDymo) {
        this.currentRenderingUri = this.getUniqueRenderingUri();
        this.store.addRendering(this.currentRenderingUri, dymoUri);
        return this.currentRenderingUri;
    }
    addConstraint(constraint) {
        if (!this.currentRenderingUri) {
            this.addRendering();
        }
        return this.store.addConstraint(this.currentRenderingUri, constraint);
    }
    addCustomParameter(typeUri, ownerUri, value) {
        return __awaiter(this, void 0, void 0, function* () {
            let uri = yield this.store.addCustomParameter(ownerUri, typeUri);
            if (!isNaN(value)) {
                this.store.setValue(uri, uris.VALUE, value);
            }
            return uri;
        });
    }
    addControl(name, type, uri, initialValue) {
        return __awaiter(this, void 0, void 0, function* () {
            uri = yield this.store.addControl(name, type, uri);
            if (!isNaN(initialValue)) {
                yield this.store.setValue(uri, uris.VALUE, initialValue);
            }
            return uri;
        });
    }
    addRampControl(initialValue, duration, frequency, name) {
        return __awaiter(this, void 0, void 0, function* () {
            let uri = yield this.addControl(name, uris.RAMP, null, initialValue);
            yield this.store.setValue(uri, uris.HAS_DURATION, duration);
            yield this.store.setControlParam(uri, uris.AUTO_CONTROL_FREQUENCY, frequency);
            return uri;
        });
    }
    addDataControl(url, jsonMap, uri) {
        return __awaiter(this, void 0, void 0, function* () {
            uri = yield this.addControl("", uris.DATA_CONTROL, uri);
            yield this.store.setValue(uri, uris.HAS_URL, url);
            yield this.store.setValue(uri, uris.HAS_JSON_MAP, jsonMap);
            return uri;
        });
    }
    addNavigator(navigatorType, variableUri) {
        return this.store.addNavigator(this.currentRenderingUri, navigatorType, variableUri);
    }
    getCurrentTopDymo() {
        return this.currentTopDymo;
    }
    setSummarizingMode(mode) {
        this.summarizingMode = mode;
    }
    setCurrentSourcePath(path) {
        this.currentSourcePath = path;
    }
    addDeepCopy(dymoUri, parentUri) {
        return __awaiter(this, void 0, void 0, function* () {
            const source = yield this.store.getSourcePath(dymoUri);
            const type = yield this.store.findObject(dymoUri, uris.TYPE);
            const copyUri = yield this.addDymo(parentUri, source, type);
            const features = yield this.store.findAllObjects(dymoUri, uris.HAS_FEATURE);
            yield Promise.all(features.map((f) => __awaiter(this, void 0, void 0, function* () {
                const type = yield this.store.findObject(f, uris.TYPE);
                const value = yield this.store.findObjectValue(f, uris.VALUE);
                yield this.setDymoFeature(copyUri, type, value);
            })));
            const params = yield this.store.findAllObjects(dymoUri, uris.HAS_PARAMETER);
            yield Promise.all(params.map((p) => __awaiter(this, void 0, void 0, function* () {
                const type = yield this.store.findObject(p, uris.TYPE);
                const value = yield this.store.findObjectValue(p, uris.VALUE);
                yield this.setDymoParameter(copyUri, type, value);
            })));
            const parts = yield this.store.findParts(dymoUri);
            yield Promise.all(parts.map((p) => __awaiter(this, void 0, void 0, function* () {
                yield this.addDeepCopy(p, copyUri);
            })));
            return copyUri;
        });
    }
    addDymo(parentUri, sourcePath, dymoType, dymoUri) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!dymoUri) {
                dymoUri = this.getUniqueDymoUri();
            }
            dymoUri = yield this.store.addDymo(dymoUri, parentUri, null, sourcePath, dymoType);
            if (!parentUri) {
                this.currentTopDymo = dymoUri;
            }
            return dymoUri;
        });
    }
    addConjunction(parentUri, partUris) {
        return __awaiter(this, void 0, void 0, function* () {
            var uri = yield this.addDymo(parentUri, null, uris.CONJUNCTION);
            yield Promise.all(partUris.map(p => this.store.addPart(uri, p)));
            return uri;
        });
    }
    addEvent(parentUri, targetUri, value) {
        return __awaiter(this, void 0, void 0, function* () {
            const uri = yield this.addDymo(parentUri, null, uris.EVENT);
            yield this.store.addTriple(uri, uris.HAS_TARGET, targetUri);
            yield this.store.setValue(uri, uris.VALUE, value);
            return uri;
        });
    }
    addFeature(name, data, dymoUri) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!dymoUri) {
                dymoUri = this.currentTopDymo;
            }
            //Benchmarker.startTask("addFeature")
            this.initTopDymoIfNecessary();
            //var feature = this.getFeature(name);
            //iterate through all levels and add averages
            var dymos = yield this.store.findAllObjectsInHierarchy(dymoUri);
            for (var i = 0; i < dymos.length; i++) {
                var currentTime = yield this.store.findFeatureValue(dymos[i], uris.TIME_FEATURE);
                var currentDuration = yield this.store.findFeatureValue(dymos[i], uris.DURATION_FEATURE);
                var currentValues = data;
                if (!isNaN(currentTime) && currentTime != null) {
                    //only filter data id time given
                    currentValues = currentValues.filter(x => currentTime <= x.time && (isNaN(currentDuration) || x.time < currentTime + currentDuration));
                }
                //event-based feature:
                if (currentValues.length < 1) {
                    var earlierValues = data.filter(x => x.time.value <= currentTime);
                    if (earlierValues.length > 0) {
                        currentValues = [_.last(earlierValues)];
                    }
                    else {
                        //set to first value
                        currentValues = [data[0]];
                    }
                }
                var value = this.getSummarizedValues(_.cloneDeep(currentValues));
                /*if (typeof value == "string") {
                    var labelFeature = getFeature(SEGMENT_LABEL);
                    this.setDymoFeature(dymos[i], getFeature(SEGMENT_LABEL), value);
                }*/
                this.setDymoFeature(dymos[i], uris.CONTEXT_URI + name, value);
            }
        });
    }
    //summarizes the given vectors into one based on summarizingMode
    getSummarizedValues(features) {
        if (features && features.length > 0) {
            //get values out, convert to arrays
            let vectors = features.map(v => v.value.length ? v.value : [v.value]);
            //summarize dimension by dimension
            let summary = _.zip(...vectors).map(v => {
                if (typeof v[0] == "string" || this.summarizingMode == globals_1.SUMMARY.FIRST) {
                    return v[0];
                }
                else if (this.summarizingMode == globals_1.SUMMARY.MEAN) {
                    return _.mean(v);
                }
                else if (this.summarizingMode == globals_1.SUMMARY.MEDIAN) {
                    return math.median(v);
                }
                else if (this.summarizingMode == globals_1.SUMMARY.MODE) {
                    return math.mode(v);
                }
            });
            summary = summary[0];
            if (summary.length == 1) {
                return summary[0];
            }
            return summary;
        }
        return 0;
    }
    addSegmentation(segments, dymoUri) {
        return __awaiter(this, void 0, void 0, function* () {
            this.initTopDymoIfNecessary();
            //var maxLevel = await this.store.findMaxLevel(this.currentTopDymo);
            if (!dymoUri)
                dymoUri = this.currentTopDymo;
            var parentMap = yield this.recursiveCreateParentMap(dymoUri);
            //console.log(JSON.stringify(parentMap))
            for (var i = 0; i < segments.length; i++) {
                var parent = this.getSuitableParent(segments[i].time.value, parentMap);
                var startTime = segments[i].time.value;
                var duration;
                if (segments[i].duration) {
                    duration = segments[i].duration.value;
                }
                else if (segments[i + 1]) {
                    duration = segments[i + 1].time.value - startTime;
                }
                else {
                    var parentTime = parent.time;
                    var parentDuration = parent.duration;
                    if (parentTime && parentDuration) {
                        duration = parentTime + parentDuration - startTime;
                    }
                }
                //don't want anything with duration 0 (what other feature values would it have?)
                if (duration > 0) {
                    var newDymoUri = yield this.addDymo(parent.uri);
                    this.setDymoFeature(newDymoUri, uris.TIME_FEATURE, startTime);
                    this.setDymoFeature(newDymoUri, uris.DURATION_FEATURE, duration);
                    /*if (segments[i].label && !isNaN(segments[i].label)) {
                        this.setDymoFeature(newDymoUri, SEGMENT_LABEL_FEATURE, segments[i].label);
                    }*/
                    this.updateParentDuration(parent, { uri: newDymoUri, time: startTime, duration: duration, parts: [] });
                }
            }
        });
    }
    recursiveCreateParentMap(topDymoUri) {
        return __awaiter(this, void 0, void 0, function* () {
            let parts = yield this.store.findParts(topDymoUri);
            /*TODO SORT?? let times = parts.map(p => this.store.findFeatureValue(p, uris.TIME_FEATURE));
            let sortedTimesAndParts = _.zip(times, parts).sort((p,q) => p[0]-q[0]);
            [times, parts] = _.unzip(sortedTimesAndParts);*/
            return {
                uri: topDymoUri,
                time: yield this.store.findFeatureValue(topDymoUri, uris.TIME_FEATURE),
                duration: yield this.store.findFeatureValue(topDymoUri, uris.DURATION_FEATURE),
                parts: parts.length > 0 ? yield Promise.all(parts.map(p => this.recursiveCreateParentMap(p))) : []
            };
        });
    }
    initTopDymoIfNecessary() {
        if (this.dymoCount == 0) {
            this.currentTopDymo = this.addDymo(null, this.currentSourcePath);
        }
    }
    getSuitableParent(time, parentMap) {
        let suitableParent = parentMap;
        while (suitableParent.parts.length > 0) {
            suitableParent.parts.every((p, i) => {
                let eligible = p.time <= time || i == 0;
                if (eligible) {
                    suitableParent = p;
                }
                return eligible;
            });
        }
        return suitableParent;
    }
    updateParentDuration(parent, newDymo) {
        if (isNaN(parent.time) || Array.isArray(parent.time) || newDymo.time < parent.time) {
            parent.time = newDymo.time;
            this.setDymoFeature(parent.uri, uris.TIME_FEATURE, parent.time);
        }
        if (isNaN(parent.duration) || Array.isArray(parent.duration) || parent.time + parent.duration < newDymo.time + newDymo.duration) {
            parent.duration = newDymo.time + newDymo.duration - parent.time;
            this.setDymoFeature(parent.uri, uris.DURATION_FEATURE, parent.duration);
        }
    }
    setDymoFeature(dymoUri, featureUri, value) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!(yield this.store.findObject(featureUri, uris.TYPE))) {
                yield this.store.addTriple(featureUri, uris.TYPE, uris.FEATURE_TYPE);
            }
            return this.store.setFeature(dymoUri, featureUri, value);
            //this.updateMinMax(featureUri, value);
        });
    }
    setDymoParameter(dymoUri, parameterUri, value) {
        return this.store.setParameter(dymoUri, parameterUri, value);
        //this.updateMinMax(featureUri, value);
    }
    getUniqueDymoUri() {
        if (this.useUuids) {
            return uris.CONTEXT_URI + uuidv4();
        }
        var dymoUri = uris.CONTEXT_URI + "dymo" + this.dymoCount;
        this.dymoCount++;
        return dymoUri;
    }
    getUniqueRenderingUri() {
        if (this.useUuids) {
            return uris.CONTEXT_URI + uuidv4();
        }
        var renderingUri = uris.CONTEXT_URI + "rendering" + this.renderingCount;
        this.renderingCount++;
        return renderingUri;
    }
}
exports.DymoGenerator = DymoGenerator;



//////////////////
// WEBPACK FOOTER
// ./~/dymo-core/lib/generator/dymo-generator.js
// module id = 1295
// module chunks = 3